import { forwardRef, useMemo, useState, useCallback, useEffect } from 'react';
import { getOrientedSourceDims } from '../../../../utils/sourceDims';

/**
 * Determines which image source to display based on current state.
 *
 * Priority:
 * 0. original: Before/after comparison mode - show original
 * When showing full image (showVizImage or cropMode):
 *   1. vips-processed: Has adjustments and wasm-vips has processed
 *   2. vips-fallback: Has adjustments but wasm-vips still processing
 *   3. viz: Full uncropped image
 * When showing cropped image:
 *   4. local-crop: Pending crop + viz URL -- CSS-crop locally
 *   5. edited: Default edited/cropped image (server-generated)
 * 6. fallback: No image available
 */
function getImageMode({ hasAdjustments, vipsProcessedUrl, currentVizUrl, currentEditedUrl, showVizImage, cropMode, showOriginal, originalUrl, cropData }) {
  // Before/after comparison takes priority
  if (showOriginal && originalUrl && hasAdjustments) return 'original';
  // Full-image modes: viz overlay, crop mode, or adjustments active while viewing full
  if (showVizImage || cropMode) {
    if (hasAdjustments && vipsProcessedUrl) return 'vips-processed';
    if (hasAdjustments && currentVizUrl) return 'vips-fallback';
    return currentVizUrl ? 'viz' : 'fallback';
  }
  // Local crop preview: use viz image + CSS transforms instead of server image
  if (cropData && currentVizUrl) return 'local-crop';
  if (currentEditedUrl) return 'edited';
  return 'fallback';
}

/**
 * Compute CSS transform to show a cropped region of the viz image.
 * The viz image is scaled and translated so only the crop region is visible
 * inside a container with the crop's aspect ratio.
 */
function computeLocalCropTransform(cropXYXY, sourceW, sourceH, imgNaturalW, imgNaturalH, containerW, containerH) {
  const [x1, y1, x2, y2] = cropXYXY;
  const cropW = x2 - x1;
  const cropH = y2 - y1;

  if (cropW <= 0 || cropH <= 0 || sourceW <= 0 || sourceH <= 0) return null;

  // Frame dimensions for the crop's aspect ratio
  const cropAspect = cropW / cropH;
  let frameW = containerW;
  let frameH = frameW / cropAspect;
  if (frameH > containerH) {
    frameH = containerH;
    frameW = frameH * cropAspect;
  }

  // Scale: the full viz image is scaled so the crop region exactly fills the frame
  const scaleX = frameW / (cropW * (imgNaturalW / sourceW));
  const scaleY = frameH / (cropH * (imgNaturalH / sourceH));
  const scale = Math.min(scaleX, scaleY);

  // The crop center in natural-pixel space
  const cropCenterNatX = ((x1 + x2) / 2) * (imgNaturalW / sourceW);
  const cropCenterNatY = ((y1 + y2) / 2) * (imgNaturalH / sourceH);

  // Translate so crop center is at frame center
  const imgScaledW = imgNaturalW * scale;
  const imgScaledH = imgNaturalH * scale;
  const translateX = frameW / 2 - cropCenterNatX * scale;
  const translateY = frameH / 2 - cropCenterNatY * scale;

  return { frameW, frameH, imgScaledW, imgScaledH, translateX, translateY, scale };
}

/**
 * ImageDisplay - Renders the main image with appropriate source and processing indicators.
 *
 * Single responsibility: Display the correct image based on current mode.
 * Extracted from Edit.jsx to reduce nested ternary complexity.
 */
const ImageDisplay = forwardRef(function ImageDisplay({
  // Image URLs
  vipsProcessedUrl,
  currentVizUrl,
  currentEditedUrl,
  originalUrl,
  // State flags
  hasAdjustments,
  vipsProcessing,
  vipsReady: _vipsReady,
  showVizImage,
  cropMode,
  showOriginal,
  // Crop data for local preview
  pendingCrop,
  cropBox,
  metaDoc,
  // Container dimensions for local crop preview
  containerDims,
  // Callbacks
  onLoad,
  onError,
  onLayoutChange,
}, ref) {
  // Track viz image natural dimensions for local crop rendering
  const [vizNatDims, setVizNatDims] = useState(null);

  const handleVizLoad = useCallback((e) => {
    if (e?.target) {
      setVizNatDims({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    }
    onLoad?.(e);
  }, [onLoad]);

  // Determine crop data for local CSS-crop preview
  const cropData = useMemo(() => {
    // Use pendingCrop if available, otherwise saved cropBox
    const crop = pendingCrop?.crop;
    const savedCrop = cropBox ? [cropBox.x1, cropBox.y1, cropBox.x2, cropBox.y2] : null;
    const cropXYXY = crop || savedCrop;
    if (!cropXYXY) return null;

    const oriented = getOrientedSourceDims(metaDoc);
    const sourceW = cropBox?.imgW || oriented?.width;
    const sourceH = cropBox?.imgH || oriented?.height;
    if (!sourceW || !sourceH) return null;

    return { cropXYXY, sourceW, sourceH, rotation: pendingCrop?.rotation || 0 };
  }, [pendingCrop, cropBox, metaDoc]);

  const mode = useMemo(
    () => getImageMode({ hasAdjustments, vipsProcessedUrl, currentVizUrl, currentEditedUrl, showVizImage, cropMode, showOriginal, originalUrl, cropData }),
    [hasAdjustments, vipsProcessedUrl, currentVizUrl, currentEditedUrl, showVizImage, cropMode, showOriginal, originalUrl, cropData]
  );

  // Emit layout bounds when in local-crop mode so face overlays align correctly.
  // Clear layout override when leaving local-crop so updateImgBounds can take over.
  useEffect(() => {
    if (mode !== 'local-crop') {
      onLayoutChange?.(null);
      return;
    }
    if (!vizNatDims || !containerDims || !cropData) return;
    const nW = vizNatDims.w;
    const nH = vizNatDims.h;
    const containerW = containerDims.w || 1200;
    const containerH = containerDims.h || 800;
    const transform = computeLocalCropTransform(
      cropData.cropXYXY, cropData.sourceW, cropData.sourceH,
      nW, nH, containerW, containerH
    );
    if (transform) {
      const frameX = (containerW - transform.frameW) / 2;
      const frameY = (containerH - transform.frameH) / 2;
      onLayoutChange?.({
        x: frameX,
        y: frameY,
        w: transform.frameW,
        h: transform.frameH,
        nW: cropData.sourceW,
        nH: cropData.sourceH,
      });
    }
  }, [mode, vizNatDims, containerDims, cropData, onLayoutChange]);

  const handleVizError = () => {
    onError?.();
  };

  switch (mode) {
    case 'original':
      return (
        <>
          <img
            ref={ref}
            src={originalUrl}
            alt="original"
            crossOrigin="anonymous"
            onLoad={onLoad}
          />
          <div className="comparison-label">Original</div>
        </>
      );

    case 'vips-processed':
      return (
        <>
          <img
            ref={ref}
            src={vipsProcessedUrl}
            alt="processed"
            onLoad={onLoad}
          />
          {vipsProcessing && (
            <div className="vips-processing-overlay">
              <div className="vips-loader">
                <div className="vips-loader-ring" />
                <span>Processing</span>
              </div>
            </div>
          )}
        </>
      );

    case 'vips-fallback':
      return (
        <>
          <img
            ref={ref}
            src={currentVizUrl}
            alt="viz"
            crossOrigin="anonymous"
            onLoad={onLoad}
          />
          {vipsProcessing && (
            <div className="vips-processing-overlay">
              <div className="vips-loader">
                <div className="vips-loader-ring" />
                <span>Processing</span>
              </div>
            </div>
          )}
        </>
      );

    case 'viz':
      return (
        <img
          ref={ref}
          src={currentVizUrl}
          alt="viz"
          onLoad={handleVizLoad}
          onError={handleVizError}
        />
      );

    case 'local-crop': {
      // CSS-crop the viz image locally instead of loading server-generated cropped image
      // This provides instant preview after confirming a recrop
      const nW = vizNatDims?.w;
      const nH = vizNatDims?.h;

      // If we don't have natural dims yet, render the image hidden to measure it
      if (!nW || !nH) {
        return (
          <img
            ref={ref}
            src={currentVizUrl}
            alt="local crop"
            onLoad={handleVizLoad}
            onError={handleVizError}
          />
        );
      }

      // Use actual container dimensions for computing the frame
      const containerW = containerDims?.w || 1200;
      const containerH = containerDims?.h || 800;
      const transform = computeLocalCropTransform(
        cropData.cropXYXY, cropData.sourceW, cropData.sourceH,
        nW, nH, containerW, containerH
      );

      if (!transform) {
        // Fallback to server image
        return (
          <img
            ref={ref}
            src={currentEditedUrl || currentVizUrl}
            alt="edited"
            onLoad={onLoad}
          />
        );
      }

      return (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${transform.frameW}px`,
            height: `${transform.frameH}px`,
            overflow: 'hidden',
          }}
        >
          <img
            ref={ref}
            src={currentVizUrl}
            alt="local crop"
            style={{
              position: 'absolute',
              width: `${transform.imgScaledW}px`,
              height: `${transform.imgScaledH}px`,
              left: `${transform.translateX}px`,
              top: `${transform.translateY}px`,
              maxWidth: 'none',
              maxHeight: 'none',
              transform: cropData.rotation ? `rotate(${cropData.rotation}deg)` : undefined,
              transformOrigin: 'center center',
            }}
            onLoad={onLoad}
            onError={handleVizError}
          />
        </div>
      );
    }

    case 'edited':
      return (
        <img
          ref={ref}
          src={currentEditedUrl}
          alt="edited"
          onLoad={onLoad}
        />
      );

    case 'fallback':
    default:
      return (
        <div className="thumb-fallback" style={{ height: "320px" }}>
          Preview not ready
        </div>
      );
  }
});

export default ImageDisplay;
